// ============================================================
// NARRATIVE FORMATTER - Pure string-building for combat log entries.
// Extracted from useFirestoreSync syncNarrativeToFirestore
// and DeathWatchSimulator.jsx doCopyNarrative.
// Zero React dependency.
// ============================================================
import { classifyOutcome, hordeLostMagnitude, OUTCOME_LABELS } from './structuredNarrative';

export interface DefenceStepSummary {
  type: string;
  label: string;
  roll: number;
  skill: number;
  success: boolean;
  dos: number;
  dof: number;
  avoided: number;
  counterAttackTriggered: boolean;
}

export interface NarrativeInput {
  type: string;
  text?: string;
  targetName?: string;
  slotName?: string;
  fireMode?: string;
  totalDamage?: number;
  woundsAfter?: number;
  woundsMax?: number;
  isHorde?: boolean;
  magDmg?: number;
  magAfter?: number;
  magMax?: number;
  skillBreakdown?: { label: string; value: number }[];
  result?: {
    jam?: boolean;
    success?: boolean;
    attackRoll?: number;
    modifiedSkill?: number;
    dos?: number;
    dof?: number;
    totalHits?: number;
    hits?: unknown[];
    hitsAfterDefence?: HitSummary[];
    dodgeResult?: { roll: number; skill: number; success: boolean; avoided: number } | null;
    parryResult?: { roll: number; skill: number; success: boolean; avoided: number } | null;
    defenceChainSteps?: DefenceStepSummary[];
    specialNotes?: string[];
    overheatDmg?: number;
  };
  [key: string]: unknown;
}

export interface HitSummary {
  location?: { name?: string; id?: string };
  rawDamage?: number;
  effectiveAP?: number;
  tb?: number;
  effectiveDamage?: number;
  ffBlocked?: boolean;
  crit?: { text?: string; isDead?: boolean } | null;
  critSev?: number;
  [key: string]: unknown;
}

export interface FirestoreNarrativeDoc {
  type: string;
  text: string;
  timestamp: number;
  userName: string;
  targetName?: string;
}

type AttackResult = NonNullable<NarrativeInput['result']>;
type DefenceRoll = { roll: number; skill: number; success: boolean; avoided: number };

/** The shared "(mode). Rolled X vs Y." clause of the log lines. */
function rolledClause(entry: NarrativeInput, r: AttackResult): string {
  return ' (' + (entry.fireMode || '?') + '). Rolled ' + r.attackRoll + ' vs ' + r.modifiedSkill + '. ';
}

/** A miss: its line, any overheat, then the special notes. */
function missLog(entry: NarrativeInput, r: AttackResult): string {
  const lines = ['MISS ' + (entry.slotName || '?') + rolledClause(entry, r) + (r.dof || 0) + ' DoF.'];
  if (r.overheatDmg) lines.push('  Overheats: ' + r.overheatDmg + ' E!');
  if (r.specialNotes) r.specialNotes.forEach((n: string) => lines.push('  ' + n));
  return lines.join('\n');
}

/** A legacy dodge or parry line; `saved` is the verb for a success. */
function legacyDefenceLine(label: string, saved: string, d: DefenceRoll): string {
  return '  ' + label + ': ' + d.roll + ' vs ' + d.skill + (d.success ? ' - ' + saved + ' ' + d.avoided : ' - failed!');
}

/** One defence-chain step's line, followed by its counter-attack line when it earned one. */
function chainStepLines(step: DefenceStepSummary): string[] {
  const tag = step.success ? 'avoided ' + step.avoided : 'failed!';
  const line = '  ' + step.label + ': ' + step.roll + ' vs ' + step.skill + ' - ' + tag;
  return step.counterAttackTriggered ? [line, '  \u2694 Counter-Attack available!'] : [line];
}

/** The defence lines: every chain step (and its counter-attack), or the legacy dodge then parry. */
function defenceLines(r: AttackResult): string[] {
  if (r.defenceChainSteps && r.defenceChainSteps.length > 0) return r.defenceChainSteps.flatMap(chainStepLines);
  const lines: string[] = [];
  if (r.dodgeResult) lines.push(legacyDefenceLine('Dodge', 'avoided', r.dodgeResult));
  if (r.parryResult) lines.push(legacyDefenceLine('Parry', 'deflected', r.parryResult));
  return lines;
}

/** The crit tail of a hit line: severity, the first 80 characters, and a lethal mark. */
function critSuffix(h: HitSummary): string {
  if (!h.crit) return '';
  return ' | CRIT(Sev ' + h.critSev + '): ' + (h.crit.text || '').substring(0, 80) + (h.crit.isDead ? ' [LETHAL]' : '');
}

/** One hit's line: a force-field block, or the damage arithmetic with any crit. */
function hitLine(h: HitSummary): string {
  const where = '  ' + (h.location?.name || '?') + ': ';
  if (h.ffBlocked) return where + 'FORCE FIELD blocked' + (h.ffOverloaded ? ' [OVERLOADED - field deactivated!]' : '');
  let s =
    where +
    (h.rawDamage || 0) +
    ' raw - ' +
    (h.effectiveAP || 0) +
    ' AP - ' +
    (h.tb || 0) +
    ' TB = ' +
    (h.effectiveDamage || 0);
  if (h.effectiveDamage === 0) s += ' (absorbed)';
  return s + critSuffix(h);
}

/** The closing magnitude or wound tally, or null when nothing was lost. */
function tallyLine(entry: NarrativeInput): string | null {
  if (hordeLostMagnitude(entry)) {
    const mag = (entry.magAfter || 0) <= 0 ? 'HORDE DESTROYED!' : 'Mag: ' + entry.magAfter + '/' + entry.magMax;
    return '  Mag dmg: ' + entry.magDmg + '. ' + mag;
  }
  if ((entry.totalDamage || 0) <= 0) return null;
  const wounds =
    (entry.woundsAfter || 0) < 0
      ? 'CRITICAL! Sev ' + Math.abs(entry.woundsAfter || 0)
      : 'Remaining: ' + entry.woundsAfter + '/' + entry.woundsMax;
  return '  Total: ' + entry.totalDamage + ' wounds. ' + wounds;
}

/** A successful attack: the outcome line, notes, defence, each hit, then the tally. */
function hitLog(entry: NarrativeInput, r: AttackResult): string {
  const label = OUTCOME_LABELS[classifyOutcome(r, entry.totalDamage || 0)];
  const lines = [
    label + ' ' + (entry.slotName || '?') + rolledClause(entry, r) + r.dos + ' DoS = ' + r.totalHits + ' hit(s).',
  ];
  if (r.specialNotes) r.specialNotes.forEach((n) => lines.push('  ' + n));
  lines.push(...defenceLines(r));
  if (r.hitsAfterDefence) lines.push(...r.hitsAfterDefence.map(hitLine));
  const tally = tallyLine(entry);
  if (tally !== null) lines.push(tally);
  return lines.join('\n');
}

/**
 * Format an attack-result narrative entry into a multi-line text summary.
 * Used by both Firestore sync and clipboard formatting.
 */
export function formatAttackResult(entry: NarrativeInput): string {
  const r = entry.result;
  if (!r) return '[attack-result]';
  if (r.jam) {
    const overheat = r.overheatDmg ? ' Overheats: ' + r.overheatDmg + ' E!' : '';
    return 'JAM! ' + (entry.slotName || '?') + ' jams (rolled ' + r.attackRoll + ').' + overheat;
  }
  if (!r.success) return missLog(entry, r);
  return hitLog(entry, r);
}

/**
 * Format a narrative entry for Firestore storage.
 * Converts rich entry objects into flat { type, text, timestamp, userName } docs.
 */
export function formatForFirestore(entry: NarrativeInput, userName: string): FirestoreNarrativeDoc {
  const doc: FirestoreNarrativeDoc = {
    type: entry.type || 'info',
    text: '',
    timestamp: (entry.timestamp as number) || Date.now(),
    userName,
  };

  if (entry.targetName) doc.targetName = entry.targetName;

  if (entry.type === 'info') {
    doc.text = entry.text || '';
  } else if (entry.type === 'attack-result') {
    doc.text = formatAttackResult(entry);
  } else {
    doc.text = entry.text || '';
  }

  if (!doc.text) doc.text = '[' + (entry.type || 'unknown') + ']';

  return doc;
}

/** The shared "(mode) rolled X vs Y." clause of the clipboard lines. */
function clipRolledClause(entry: NarrativeInput, r: AttackResult): string {
  return ' (' + (entry.fireMode || '') + ') rolled ' + r.attackRoll + ' vs ' + r.modifiedSkill + '. ';
}

/** A miss on the clipboard: its line, any overheat, then the special notes. */
function clipMiss(entry: NarrativeInput, r: AttackResult): string {
  const lines = ['MISS: ' + (entry.slotName || '') + clipRolledClause(entry, r) + r.dof + ' DoF.'];
  if (r.overheatDmg) lines.push('  Overheats: ' + r.overheatDmg + ' E!');
  if (r.specialNotes) r.specialNotes.forEach((n: string) => lines.push('  ' + n));
  return lines.join('\n');
}

/** One chain step as an inline clipboard clause, with its counter-attack mark. */
function clipChainStep(step: DefenceStepSummary): string {
  const txt = ' ' + step.label + ': ' + (step.success ? 'avoided ' + step.avoided : 'failed') + '.';
  return step.counterAttackTriggered ? txt + ' [Counter-Attack!]' : txt;
}

/** A legacy dodge or parry as an inline clipboard clause, or '' when it was not rolled. */
function clipLegacy(label: string, saved: string, d: DefenceRoll | null | undefined): string {
  if (!d) return '';
  return ' ' + label + ': ' + (d.success ? saved + ' ' + d.avoided : 'failed') + '.';
}

/** The inline defence clauses: every chain step, or the legacy dodge then parry. */
function clipDefence(r: AttackResult): string {
  if (r.defenceChainSteps && r.defenceChainSteps.length > 0) return r.defenceChainSteps.map(clipChainStep).join('');
  return clipLegacy('Dodge', 'avoided', r.dodgeResult) + clipLegacy('Parry', 'deflected', r.parryResult);
}

/** One hit on the clipboard, each on its own line. */
function clipHit(h: HitSummary): string {
  const where = '\n  ' + (h.location?.name || '') + ': ';
  if (h.ffBlocked) return where + 'FORCE FIELD blocked.' + (h.ffOverloaded ? ' [OVERLOADED]' : '');
  return (
    where + (h.effectiveDamage || 0) + ' dmg' + (h.crit ? ' [CRIT Sev ' + h.critSev + ': ' + h.crit.text + ']' : '')
  );
}

/** The closing magnitude or wound tally on the clipboard, or '' when nothing was lost. */
function clipTally(entry: NarrativeInput): string {
  if (hordeLostMagnitude(entry)) {
    return '\n  Magnitude: -' + entry.magDmg + ' (' + entry.magAfter + '/' + entry.magMax + ')';
  }
  if ((entry.totalDamage || 0) <= 0) return '';
  const wounds =
    (entry.woundsAfter || 0) < 0
      ? 'CRITICAL Sev ' + Math.abs(entry.woundsAfter || 0)
      : entry.woundsAfter + '/' + entry.woundsMax + ' remaining';
  return '\n  Total: ' + entry.totalDamage + ' wounds. ' + wounds;
}

/** A jam on the clipboard, with any overheat damage. */
function clipJam(entry: NarrativeInput, r: AttackResult): string {
  const overheat = r.overheatDmg ? ' Overheats: ' + r.overheatDmg + ' E dmg.' : '';
  return 'JAM! ' + (entry.slotName || '') + ' jams (rolled ' + r.attackRoll + ').' + overheat;
}

/** An attack result on the clipboard: jam, miss, or the hit with defence, hits and tally. */
function clipAttack(entry: NarrativeInput, r: AttackResult): string {
  if (r.jam) return clipJam(entry, r);
  if (!r.success) return clipMiss(entry, r);
  const label = OUTCOME_LABELS[classifyOutcome(r, entry.totalDamage || 0)];
  let txt =
    label + ' ' + (entry.slotName || '') + clipRolledClause(entry, r) + r.dos + ' DoS = ' + r.totalHits + ' hit(s).';
  txt += clipDefence(r);
  if (r.hitsAfterDefence) txt += r.hitsAfterDefence.map(clipHit).join('');
  return txt + clipTally(entry);
}

/**
 * Format a local narrative entry for clipboard text.
 * Simpler format than Firestore - used by doCopyNarrative.
 */
export function formatForClipboardEntry(entry: NarrativeInput): string {
  if (entry.type === 'info') return entry.text || '';
  if (entry.type !== 'attack-result' || !entry.result) return '';
  return clipAttack(entry, entry.result);
}

/**
 * Format full combat log for clipboard copy.
 * Combines local narrative log + Firestore shared log into one text block.
 */
export function formatForClipboard(
  localLog: NarrativeInput[],
  firestoreLog: { userName?: string; text?: string }[],
  targetName: string,
): string {
  const lines = localLog.map((e) => formatForClipboardEntry(e)).filter(Boolean);

  const fsLines = firestoreLog.map((e) => '[' + (e.userName || '?') + '] ' + (e.text || '')).filter(Boolean);

  return (
    '=== DEATHWATCH COMBAT LOG ===\nTarget: ' +
    (targetName || 'Unknown') +
    '\n' +
    new Date().toLocaleString() +
    '\n\n' +
    lines.join('\n') +
    (fsLines.length > 0 ? '\n\n=== SHARED SESSION LOG ===\n' + fsLines.join('\n') : '')
  );
}
